import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, FileSpreadsheet, ShieldCheck, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Field, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { handleApiError } from '@/api/client'
import {
  approveCrmImport,
  cancelCrmImport,
  fetchCrmImportBatches,
  previewCrmImport,
  queueCrmImport,
  type CrmImportBatch,
  type CrmImportPreview,
} from '@/api/lcCrm'
import { useAuthStore } from '@/store/authStore'
import { formatKstDateTime } from './crmLabels'
import { CrmRebuildCard } from './CrmRebuildCard'

const ACTIVE_JOB_STATES = new Set(['QUEUED', 'RUNNING', 'CANCEL_REQUESTED'])

const REPORT_LABELS: Array<[string, string]> = [
  ['excelContactCount', 'Excel 연락처'],
  ['excelLeadCount', '리드'],
  ['excelConsultationCount', '상담'],
  ['excelTaskCount', '할 일'],
  ['attendanceOnlyConsultationsSkipped', '참석 확인 통화 제외'],
  ['excelConsultationUnmatchedCount', '상담 미매칭'],
  ['excelConsultationAmbiguousCount', '상담 모호'],
  ['excelTaskUnmatchedCount', '할 일 미매칭'],
  ['fieldConflictCount', '이름 충돌'],
]

const JOB_STATE_LABEL: Record<string, string> = {
  QUEUED: '대기',
  RUNNING: '적용 중',
  CANCEL_REQUESTED: '취소 요청',
  COMPLETED: '완료',
  FAILED: '실패',
  CANCELLED: '취소됨',
}

export function CrmImportPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const [customerFile, setCustomerFile] = useState<File | null>(null)
  const [callsFile, setCallsFile] = useState<File | null>(null)
  const [cutoffAt, setCutoffAt] = useState('')
  const [expectedChecksum, setExpectedChecksum] = useState('')
  const [approver, setApprover] = useState(user?.email ?? '')

  const batches = useQuery({
    queryKey: ['lc-crm-import-batches'],
    queryFn: fetchCrmImportBatches,
    refetchInterval: (query) => {
      const rows = query.state.data
      return rows?.some((row) => row.jobState && ACTIVE_JOB_STATES.has(row.jobState)) ? 2_000 : false
    },
  })

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!customerFile || !callsFile || !cutoffAt) {
        throw new Error('고객 마스터, 통화 트래커, cutoff를 모두 입력하세요.')
      }
      return previewCrmImport({
        customerFile,
        callsFile,
        owner: user?.email ?? user?.fullName ?? 'admin',
        cutoffAt: new Date(`${cutoffAt}:00+09:00`).toISOString(),
        expectedCanonicalChecksum: expectedChecksum.trim() || undefined,
      })
    },
    onError: (error) => toast.error(handleImportError(error)),
  })

  const applyMutation = useMutation({
    mutationFn: async (preview: CrmImportPreview) => {
      if (!approver.trim()) throw new Error('승인자를 입력하세요.')
      const canonicalChecksum = String(preview.previewReport.canonicalChecksum ?? '')
      if (!canonicalChecksum) throw new Error('미리보기에 canonical checksum이 없습니다.')
      await approveCrmImport(preview.batchId, approver.trim())
      return queueCrmImport(preview.batchId, expectedChecksum.trim() || canonicalChecksum)
    },
    onSuccess: async () => {
      toast.success('Import 작업을 예약했습니다. 페이지를 닫아도 서버에서 계속됩니다.')
      await queryClient.invalidateQueries({ queryKey: ['lc-crm-import-batches'] })
    },
    onError: (error) => toast.error(handleImportError(error)),
  })

  const cancelMutation = useMutation({
    mutationFn: cancelCrmImport,
    onSuccess: async () => {
      toast.success('대기 중인 import를 취소했습니다.')
      await queryClient.invalidateQueries({ queryKey: ['lc-crm-import-batches'] })
    },
    onError: (error) => toast.error(handleImportError(error)),
  })

  const preview = previewMutation.data

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Baseline import</h2>
        <p className="text-sm text-muted-foreground">
          고객 마스터와 통화 트래커를 검증한 뒤 비동기 작업으로 적용합니다.
        </p>
      </div>

      <Alert>
        <ShieldCheck />
        <AlertTitle>staging → production 동일성</AlertTitle>
        <AlertDescription>
          staging 미리보기의 canonical checksum을 production의 예상 checksum에 붙여 넣으면 다른 파일은
          적용 전에 차단됩니다. 파일에는 개인정보가 있으므로 화면 밖으로 공유하지 마세요.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>1. Excel 미리보기</CardTitle>
          <CardDescription>각 파일은 20MB 이하의 .xlsx 형식이어야 합니다.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <FileField
            id="crm-customer-file"
            label="고객 마스터"
            file={customerFile}
            onChange={setCustomerFile}
          />
          <FileField
            id="crm-calls-file"
            label="통화 트래커"
            file={callsFile}
            onChange={setCallsFile}
          />
          <Field>
            <FieldLabel htmlFor="crm-import-cutoff">Cutoff (KST)</FieldLabel>
            <Input
              id="crm-import-cutoff"
              type="datetime-local"
              value={cutoffAt}
              onChange={(event) => setCutoffAt(event.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="crm-expected-checksum">예상 canonical checksum (production 선택)</FieldLabel>
            <Input
              id="crm-expected-checksum"
              value={expectedChecksum}
              onChange={(event) => setExpectedChecksum(event.target.value)}
              placeholder="staging checksum을 붙여 넣으세요"
            />
          </Field>
          <div className="md:col-span-2">
            <Button
              disabled={previewMutation.isPending || !customerFile || !callsFile || !cutoffAt}
              onClick={() => previewMutation.mutate()}
            >
              {previewMutation.isPending ? <Spinner /> : <FileSpreadsheet className="size-4" />}
              미리보기 생성
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview ? (
        <Card>
          <CardHeader>
            <CardTitle>2. 검토·승인</CardTitle>
            <CardDescription>
              실제 CRM 쓰기는 아직 시작되지 않았습니다. 미매칭·충돌 건수를 확인하세요.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {REPORT_LABELS.map(([key, label]) => (
                <div key={key} className="rounded-lg border border-border bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums">{reportNumber(preview, key)}</p>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">Canonical checksum</p>
              <p className="mt-1 break-all font-mono text-xs">{String(preview.previewReport.canonicalChecksum ?? '-')}</p>
            </div>
            {reportWarnings(preview).length > 0 ? (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertTitle>변환 경고 {reportWarnings(preview).length}건</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-5">
                    {reportWarnings(preview).slice(0, 10).map((warning) => <li key={warning}>{warning}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}
            <div className="flex flex-wrap items-end gap-3">
              <Field className="min-w-64 flex-1">
                <FieldLabel htmlFor="crm-import-approver">승인자</FieldLabel>
                <Input
                  id="crm-import-approver"
                  value={approver}
                  onChange={(event) => setApprover(event.target.value)}
                />
              </Field>
              <Button
                disabled={applyMutation.isPending || preview.status !== 'PREVIEWED'}
                onClick={() => applyMutation.mutate(preview)}
              >
                {applyMutation.isPending ? <Spinner /> : <CheckCircle2 className="size-4" />}
                승인 후 실행 예약
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <CrmRebuildCard />

      <Card>
        <CardHeader>
          <CardTitle>Import 작업 이력</CardTitle>
          <CardDescription>적용 중인 작업은 2초 간격으로 자동 갱신됩니다.</CardDescription>
        </CardHeader>
        <CardContent>
          {batches.isLoading ? <div className="flex justify-center py-8"><Spinner /></div> : null}
          {batches.error ? (
            <Alert variant="destructive"><AlertCircle /><AlertTitle>작업 이력을 불러오지 못했습니다.</AlertTitle></Alert>
          ) : null}
          {batches.data ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>생성</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>진행</TableHead>
                  <TableHead>Checksum</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.data.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">작업이 없습니다.</TableCell></TableRow>
                ) : batches.data.map((batch) => (
                  <ImportBatchRow
                    key={batch.batchId}
                    batch={batch}
                    cancelling={cancelMutation.isPending}
                    onCancel={() => cancelMutation.mutate(batch.batchId)}
                  />
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

function FileField({ id, label, file, onChange }: {
  id: string
  label: string
  file: File | null
  onChange: (file: File | null) => void
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      />
      <p className="text-xs text-muted-foreground">{file ? `${file.name} · ${formatBytes(file.size)}` : '선택 안 됨'}</p>
    </Field>
  )
}

function ImportBatchRow({ batch, cancelling, onCancel }: {
  batch: CrmImportBatch
  cancelling: boolean
  onCancel: () => void
}) {
  const total = batch.totalCount || batch.sourceRowCount
  const processed = batch.processedCount || (batch.jobState === 'COMPLETED' ? total : 0)
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0
  const failed = batch.jobState === 'FAILED' || batch.status === 'FAILED'
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-sm">{formatKstDateTime(batch.createdAt)}</TableCell>
      <TableCell>
        <Badge variant={failed ? 'destructive' : batch.jobState === 'COMPLETED' ? 'default' : 'secondary'}>
          {batch.jobState ? JOB_STATE_LABEL[batch.jobState] ?? batch.jobState : batch.status}
        </Badge>
        {batch.errorMessage ? <p className="mt-1 max-w-64 text-xs text-destructive">{batch.errorMessage}</p> : null}
      </TableCell>
      <TableCell className="min-w-40">
        <div className="flex items-center justify-between text-xs tabular-nums text-muted-foreground">
          <span>{processed.toLocaleString('ko-KR')} / {total.toLocaleString('ko-KR')}</span><span>{percent}%</span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-primary transition-[width]" style={{ width: `${percent}%` }} />
        </div>
      </TableCell>
      <TableCell className="max-w-48 truncate font-mono text-xs" title={batch.canonicalChecksum ?? undefined}>
        {batch.canonicalChecksum ?? '-'}
      </TableCell>
      <TableCell className="text-right">
        {batch.jobState === 'QUEUED' ? (
          <Button variant="outline" size="sm" disabled={cancelling} onClick={onCancel}>
            {cancelling ? <Spinner /> : <XCircle className="size-4" />} 취소
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

function reportNumber(preview: CrmImportPreview, key: string): string {
  const value = preview.previewReport[key]
  return typeof value === 'number' ? value.toLocaleString('ko-KR') : '-'
}

function reportWarnings(preview: CrmImportPreview): string[] {
  const value = preview.previewReport.conversionWarnings
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function formatBytes(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)}KB` : `${(bytes / 1024 / 1024).toFixed(1)}MB`
}

function handleImportError(error: unknown): string {
  return error instanceof Error && !('response' in error) ? error.message : handleApiError(error)
}
